import React, { useMemo, useState } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { IEntity } from 'typings/application/entity';
import { GraphNode } from '../../../../../../../Charts/GraphChart';
import { ActionKeys } from 'components/Forms/TemplateForms/Onboarding/Components/SubmitButtons';
import { colorsTheme } from 'resources/theme/styled/colors';

// components
import ManageKYC from 'components/Additional/CRM/ManageKYC';
import Identification from './Tabs/Identification';
import GeneralInformation from './Tabs/GeneralInformation';
import { IconSVG, TabModel, Tabs } from '@ui';
import { ReactComponent as CheckIcon } from 'resources/icons/remix-icons/check-line.svg';

type TabKeys = 'generalInformation' | 'identification';

interface IProps {
  graphNode: GraphNode;
  onAddKYC: () => void;
  onAction?: (actionType: ActionKeys | null) => void;
}

const OrganizationWithoutKeyRelationship = ({
  graphNode,
  onAddKYC,
  onAction,
}: IProps) => {
  const { t } = useTranslation('onboarding');
  const [currentStep, setCurrentStep] = useState<TabKeys>('generalInformation');

  const tabs = useMemo<TabModel[]>(() => {
    return [
      {
        key: 'generalInformation',
        label: (
          <>
            {t('applicant_information.section_title')}{' '}
            <StyledIconSVG
              component={CheckIcon}
              color={colorsTheme.colorPrimary}
            />
          </>
        ),
        children: (
          <GeneralInformation
            organization={graphNode.model as IEntity}
            onAction={onAction}
          />
        ),
      },

      {
        key: 'identification',
        label: (
          <>
            {t('identification.title')}{' '}
            <StyledIconSVG
              component={CheckIcon}
              color={colorsTheme.colorPrimary}
            />
          </>
        ),
        children: (
          <Identification
            organization={graphNode.model as IEntity}
            onAction={onAction}
          />
        ),
      },
    ];
  }, [graphNode, onAction, t]);

  return (
    <>
      <ManageKYC
        onUpdate={onAddKYC}
        data={{
          nodeId: graphNode.id,
          onboardingProcessId: null,
          isOnboardingProcessRemovable:
            graphNode.metadata.isOnboardingProcessRemovable,
          isOnboardingProcessCreatable:
            graphNode.metadata.isOnboardingProcessCreatable,
        }}
      />
      <Tabs
        useChangeView
        items={tabs}
        activeKey={currentStep}
        defaultActiveKey={currentStep}
        onChange={setCurrentStep}
      />
    </>
  );
};

const StyledIconSVG = styled(IconSVG)`
  margin: 0 0 0 ${({ theme }) => theme.marginXXs} !important;
`;

export default OrganizationWithoutKeyRelationship;
